import React, { useState, useEffect } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import * as Emoji from "quill-emoji";
import "quill-emoji/dist/quill-emoji.css";
import {
  getAllBanks,
  createExchangeRate,
  fetchAllExchangeRate,
  updateExchangeRate,
  exchangeRateMessageUpdate,
} from "../../services/adminService";
import { isSameDay, set } from "date-fns";
import DashboardStatusGrid from "./DashboardStatusGrid";

Quill.register("modules/emoji", Emoji);

const quillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "image", "emoji"],
    ["clean"],
  ],
  "emoji-toolbar": true,
  "emoji-shortname": true,
};
const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "link",
  "image",
  "emoji",
];

export default function AddExchangeRate() {
  // --- your existing state ---
  const [banks, setBanks] = useState([]);
  const [source, setSource] = useState("");
  const [hashtag, setHashtag] = useState("");
  const [rateEntries, setRateEntries] = useState([
    { bankId: "", sell: "", buy: "" },
  ]);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [exchangeRateId, setExchangeRateId] = useState(null);
  const [userMessage, setUserMessage] = useState("");
  const [submitError, setSubmitError] = useState("");

  // --- new: today’s rate state & update box ---
  const [todayRate, setTodayRate] = useState(null);
  const [updateMsg, setUpdateMsg] = useState("");
  const [updateStatus, setUpdateStatus] = useState("");
  const [toast, setToast] = useState({ message: "", isError: false });

  useEffect(() => {
    if (!toast.message) return;
    const timer = setTimeout(
      () => setToast({ message: "", isError: false }),
      3000
    );
    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    const init = async () => {
      // fetch banks
      const banksResp = await getAllBanks();
      const banksData = Array.isArray(banksResp)
        ? banksResp
        : banksResp?.banks || [];
      setBanks(banksData);

      if (!isEditMode && banksData.length) {
        setRateEntries([{ bankId: banksData[0].id, sell: "", buy: "" }]);
      }

      // fetch all rates
      const ratesResp = await fetchAllExchangeRate();
      const all = ratesResp.data || [];

      // find today's
      const today = all.find((r) =>
        isSameDay(new Date(r.createdAt), new Date())
      );
      if (today) {
        setTodayRate(today);

        // also populate form for edit
        setIsEditMode(true);
        setExchangeRateId(today.id);
        setSource(today.source || "");
        setHashtag(today.hashtag || "");
        setUserMessage(today.message || "");

        // build rateEntries
        const entries = Object.entries(today.body)
          .filter(([k]) => k !== "Average")
          .map(([bankName, { sellPrice, buyPrice }]) => {
            const b = banksData.find((x) => x.name === bankName);
            return {
              bankId: b?.id || "",
              sell: sellPrice,
              buy: buyPrice,
            };
          });
        setRateEntries(entries);
      }
    };
    init();
  }, [isEditMode]);

  // your unchanged create/update handler
  // inside AddExchangeRate component, replace handleSubmit with:

  const handleSubmit = async (e) => {
    e.preventDefault();

    // validate prices
    const invalid = rateEntries.some(
      ({ sell, buy }) => parseFloat(sell) <= 0 || parseFloat(buy) <= 0
    );
    if (invalid) {
      setToast({
        message: "All buy/sell prices must be greater than zero.",
        isError: true,
      });
      setSubmitError(true);
      return;
    }

    if (hasDuplicateBanks(rateEntries)) {
      setToast({
        message: "Duplicate banks detected. Each bank can only appear once.",
        isError: true,
      });
      return;
    }

    setFormSubmitting(true);
    setSubmitError(false);

    // build the body object
    const ratesBody = {};
    let totalSell = 0,
      totalBuy = 0,
      count = 0;
    rateEntries.forEach(({ bankId, sell, buy }) => {
      const bank = banks.find((b) => String(b.id) === String(bankId));
      if (!bank) return;
      const sellPrice = parseFloat(sell);
      const buyPrice = parseFloat(buy);
      ratesBody[bank.name] = { sellPrice, buyPrice };
      totalSell += sellPrice;
      totalBuy += buyPrice;
      count++;
    });
    ratesBody.Average = {
      sellPrice: count ? totalSell / count : 0,
      buyPrice: count ? totalBuy / count : 0,
    };

    try {
      let result;
      if (isEditMode) {
        // existing today’s rate → update
        result = await updateExchangeRate(
          ratesBody,
          source.trim() || null,
          hashtag.trim() || null,
          exchangeRateId,
          userMessage
        );
        setToast({
          message: "Exchange rate updated successfully.",
          isError: false,
        });
      } else {
        // no today’s rate → create
        result = await createExchangeRate(
          ratesBody,
          source.trim() || null,
          hashtag.trim() || null,
          userMessage
        );
        setToast({
          message: "Exchange rate created successfully.",
          isError: false,
        });
      }

      // reset form & reload to pick up today’s rate
      setSource("");
      setHashtag("");
      setUserMessage("");
      setRateEntries([{ bankId: banks[0]?.id || "", sell: "", buy: "" }]);
      setIsEditMode(false);
      setExchangeRateId(null);
      window.location.reload();
    } catch (err) {
      console.error("Error saving exchange rate:", err);
      setToast({
        message: "There was an error saving the exchange rate.",
        isError: true,
      });
      setSubmitError(true);
    } finally {
      setFormSubmitting(false);
    }
  };

  // handler for the embedded update box
  const handleUpdateMessage = async () => {
    if (!todayRate) return;
    setUpdateStatus("loading");
    try {
      await exchangeRateMessageUpdate(todayRate.id, updateMsg);
      setUpdateStatus("success");
      alert("Message updated successfully!");
    } catch {
      setUpdateStatus("error");
      alert("Failed to update message.");
    }
  };

  const hasDuplicateBanks = (entries) => {
    const bankIds = entries.map((e) => e.bankId);
    return new Set(bankIds).size !== bankIds.length;
  };

  return (
    <div>
      <DashboardStatusGrid />

      <div className="flex items-center justify-center py-10">
        <div className="max-w-2xl w-full">
          <h2 className="text-3xl font-bold mb-6 text-center">
            {isEditMode ? "Edit Exchange Rate" : "Add Exchange Rate"}
          </h2>

          {toast.message && (
            <div
              className={`fixed top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded shadow z-50
      ${
        toast.isError
          ? "bg-red-100 border border-red-400 text-red-700"
          : "bg-green-100 border border-green-400 text-green-700"
      }`}
            >
              {toast.message}
              <button
                onClick={() => setToast({ message: "", isError: false })}
                className="ml-4 font-bold"
              >
                ×
              </button>
            </div>
          )}
          <form onSubmit={handleSubmit}>
            {/* Source */}
            <div className="mb-4">
              <label className="block text-gray-700 mb-1">
                Source (Optional)
              </label>
              <input
                value={source}
                onChange={(e) => setSource(e.target.value)}
                className="w-full border border-gray-300 p-2 rounded-md"
                placeholder="Enter source"
              />
            </div>

            {/* Hashtag */}
            <div className="mb-4">
              <label className="block text-gray-700 mb-1">
                Hashtag (Optional)
              </label>
              <input
                value={hashtag}
                onChange={(e) => setHashtag(e.target.value)}
                className="w-full border border-gray-300 p-2 rounded-md"
                placeholder="Enter hashtag"
              />
            </div>

            {/* — show this ONLY if there's NO todayRate — */}
            {!todayRate && (
              <div className="mb-16">
                <label className="block text-gray-700 mb-1">
                  Message (Optional)
                </label>
                <ReactQuill
                  value={userMessage}
                  onChange={setUserMessage}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Write your message..."
                  theme="snow"
                  style={{ height: "150px", marginBottom: "30px" }}
                />
              </div>
            )}

            {/* — show this ONLY if there IS a todayRate — */}
            {todayRate && (
              <div className="mb-16 border border-gray-300 p-4 rounded-md">
                <h3 className="text-lg font-semibold mb-2">
                  Update Today’s Telegram Message
                </h3>

                <p className="text-sm text-gray-600 mb-4">
                  {new Date(todayRate.createdAt).toLocaleString()}
                </p>

                <textarea
                  rows={4}
                  placeholder="Write your update here…"
                  value={updateMsg}
                  onChange={(e) => setUpdateMsg(e.target.value)}
                  className="w-full border border-gray-300 p-2 rounded-md mb-4"
                  style={{ whiteSpace: "pre-wrap" }}
                />

                <button
                  type="button"
                  onClick={handleUpdateMessage}
                  disabled={updateStatus === "loading"}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
                >
                  {updateStatus === "loading"
                    ? "Updating..."
                    : "Update Message"}
                </button>

                {updateStatus === "success" && (
                  <p className="text-green-600 mt-2">✅ Updated!</p>
                )}
                {updateStatus === "error" && (
                  <p className="text-red-600 mt-2">❌ Update failed.</p>
                )}
              </div>
            )}

            {/* Rate Entries */}
            <div className="mb-6">
              <h3 className="text-xl font-semibold mb-2">Exchange Rates</h3>
              {rateEntries.map((entry, idx) => (
                <div
                  key={idx}
                  className="flex flex-col md:flex-row items-start md:items-center gap-4 mb-4 p-4 border rounded-md"
                >
                  {/* Bank */}
                  <div className="flex-1">
                    <label className="block text-gray-700 mb-1">Bank</label>
                    <select
                      value={entry.bankId}
                      onChange={(e) => {
                        const newBankId = e.target.value;

                        // Check if bank is already selected in other entries
                        const isDuplicate = rateEntries.some(
                          (entry, index) =>
                            entry.bankId === newBankId && index !== idx
                        );

                        if (isDuplicate) {
                          setToast({
                            message: "This bank has already been selected",
                            isError: true,
                          });
                          return;
                        }

                        const a = [...rateEntries];
                        a[idx].bankId = newBankId;
                        setRateEntries(a);
                      }}
                      className="w-full border border-gray-300 p-2 rounded-md"
                    >
                      {banks
                        .filter(
                          (bank) =>
                            // Show either:
                            // - Currently selected bank (so you can't get stuck)
                            // - Banks not used in other entries
                            bank.id === entry.bankId ||
                            !rateEntries.some((e) => e.bankId === bank.id)
                        )
                        .map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.name}
                          </option>
                        ))}
                    </select>
                  </div>

                  {/* Buy */}
                  <div className="flex-1">
                    <label className="block text-gray-700 mb-1">
                      Buy Price
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.0001"
                      value={entry.buy}
                      onChange={(e) => {
                        const a = [...rateEntries];
                        a[idx].buy = e.target.value;
                        setRateEntries(a);
                      }}
                      className="w-full border border-gray-300 p-2 rounded-md"
                      placeholder="Buy Price"
                      required
                    />
                  </div>

                  {/* Sell */}
                  <div className="flex-1">
                    <label className="block text-gray-700 mb-1">
                      Sell Price
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.0001"
                      value={entry.sell}
                      onChange={(e) => {
                        const a = [...rateEntries];
                        a[idx].sell = e.target.value;
                        setRateEntries(a);
                      }}
                      className="w-full border border-gray-300 p-2 rounded-md"
                      placeholder="Sell Price"
                      required
                    />
                  </div>

                  {rateEntries.length > 1 && (
                    <button
                      type="button"
                      onClick={() =>
                        setRateEntries((r) => r.filter((_, i) => i !== idx))
                      }
                      className="text-red-500 hover:underline mt-6"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}

              <button
                type="button"
                onClick={() => {
                  // Find available banks not already in rateEntries
                  const availableBanks = banks.filter(
                    (bank) =>
                      !rateEntries.some((entry) => entry.bankId === bank.id)
                  );

                  if (availableBanks.length === 0) {
                    setToast({
                      message: "All available banks have already been added",
                      isError: true,
                    });
                    return;
                  }

                  setRateEntries((r) => [
                    ...r,
                    {
                      bankId: availableBanks[0]?.id || "",
                      sell: "",
                      buy: "",
                    },
                  ]);
                }}
                className="text-blue-600 hover:underline"
              >
                + Add another rate
              </button>
            </div>

            <button
              type="submit"
              disabled={formSubmitting}
              className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 transition"
            >
              {formSubmitting
                ? isEditMode
                  ? "Updating..."
                  : "Saving..."
                : isEditMode
                ? "Update Exchange Rate"
                : "Save Exchange Rate"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
